/**
 * Per-account "trusted device" token storage.
 *
 * The 2FA "remember this device" proof is a 30-day token the server mints and
 * binds to ONE user_id. The browser has to hold it so future logins can present
 * it and skip the email code. Cross-site cookies (Vercel SPA ↔ Render API) get
 * dropped by modern browsers as third-party, so localStorage is the reliable
 * path.
 *
 * The token MUST be keyed by account. A single shared key meant that on a
 * browser where several accounts sign in (e.g. an operator juggling owner /
 * ops-manager / pilot logins on one machine), each login overwrote the previous
 * account's token. The server then saw a token bound to the wrong user_id,
 * rejected it, and forced 2FA again — the two accounts ping-ponged, re-minting a
 * fresh device on essentially every sign-in. Scoping the key by email lets each
 * account keep its own proof side by side.
 */

const PREFIX = "altiv_trusted_device";
// The pre-scoping global key. Any value here is bound to whichever account
// minted it last and can't be attributed to an email, so we never read it —
// we only purge it on the first scoped write to avoid leaving dead state.
const LEGACY_KEY = "altiv_trusted_device";

function _normalize(email?: string | null): string {
  return (email || "").trim().toLowerCase();
}

function _key(email?: string | null): string | null {
  const e = _normalize(email);
  return e ? `${PREFIX}:${e}` : null;
}

/** The trusted-device token for this account, or null if none is stored. */
export function getTrustedDevice(email?: string | null): string | null {
  const key = _key(email);
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Persist (or clear, when token is falsy) the trusted-device token for this account. */
export function setTrustedDevice(email: string | null | undefined, token?: string | null): void {
  const key = _key(email);
  if (!key) return;
  try {
    if (token) localStorage.setItem(key, token);
    else localStorage.removeItem(key);
    // One-time cleanup: drop the legacy un-scoped key so it doesn't linger as
    // dead state once an account has migrated to its scoped key.
    if (LEGACY_KEY !== key) localStorage.removeItem(LEGACY_KEY);
  } catch {}
}
